import type { JSX } from 'react'

import { clsx } from 'clsx'

import {
  ListFilterIcon,
  PageHeader,
  pageHeaderStyles,
  Separator,
  Slot,
} from '@buffer-mono/popcorn'

import { AllChannelsTimeZoneSelect } from '~publish/components/AllChannelsTimeZoneSelect'
import { FeedbackWidget } from '~publish/components/FeedbackWidget'
import {
  FilterByChannel,
  FilterByChannel_Channel,
} from '~publish/components/FilterByChannel'
import type { MaskedFilterByChannelFragment } from '~publish/components/FilterByChannel'
import { FilterByPostType } from '~publish/components/FilterByPostType'
import { FilterByTag } from '~publish/components/FilterByTag'
import {
  UndatedDraftsToggle,
  useUndatedDraftsToggleFocusReturn,
} from '~publish/components/Calendar/UndatedDraftsToggle'
import { NewPostHeaderActions } from '~publish/components/NewPostHeaderActions'
import { POST_TYPE_FILTER_SPLIT } from '~publish/helpers/postType'
import type { FilterablePostType } from '~publish/helpers/postType'
import { PostListOrCalendarViewToggle } from '~publish/components/PostListOrCalendarViewToggle/PostListOrCalendarViewToggle'
import { TimezoneDisplay } from '~publish/components/TimezoneDisplay'
import { ViewOnlyHeaderBadge } from '~publish/components/ViewOnlyBadge'
import { readFragment } from '~publish/graphql'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import { PublishPageTitle } from '~publish/pages/AllChannels'
import { ChannelPageTitle } from '~publish/pages/Channel/ChannelPageTitle'
import { ChannelSettingsMenu } from '~publish/pages/Channel/ChannelSettingsMenu'
import { GridPreviewButton } from '~publish/pages/Channel/ChannelShopGrid/GridPreviewButton'
import {
  MoreActions,
  ShowPostingTimesMenuItem,
} from '~publish/pages/Channel/MoreActions'
import { getDisabledPublishChannelReason } from '~publish/utils/permissions/getDisabledChannelIds'
import { VIEW_ONLY_PUBLISH_CHANNEL_TOOLTIP } from '~publish/utils/permissions/viewOnlyPublish'

import { CalendarActions, PostingTimesActions } from './CalendarActions'
import { CalendarControls } from './CalendarControls'
import { MobileCalendarDateLabel } from './MobileCalendarDateLabel'
import { TypeFilter } from './TypeFilter/TypeFilter'

import styles from './CalendarHeader.module.css'

const EMPTY_POST_TYPES: readonly FilterablePostType[] = []

type CalendarHeaderProps = {
  channels: MaskedFilterByChannelFragment
  isSingleChannel: boolean
  onSelectChannels: (channelIds: string[]) => void
  onSelectTags: (tagIds: string[]) => void
  selectedChannelIds?: string[]
  disabledChannelIds?: Set<string>
  viewOnlyChannelIds?: Set<string>
  selectedTagIds?: string[]
  /**
   * Post formats the selected channel's service can be filtered by. Empty
   * unless exactly one supported channel is in scope, which hides the control.
   * @see supportedPostTypesForService
   */
  postTypeOptions?: readonly FilterablePostType[]
  selectedPostTypes?: FilterablePostType[]
  onSelectPostTypes?: (postTypes: string[]) => void
  /** Hides the composer and settings entry points (view-only access). */
  viewOnly?: boolean
  showPostingTimesControl: boolean
  /** Shows the View Only badge in the left-most actions slot. */
  showViewOnlyBadge?: boolean
  isUndatedDraftsOpen?: boolean
  isUndatedDraftsSidebarMounted?: boolean
  undatedDraftsSidebarId?: string
  undatedDraftsCount?: number
  onToggleUndatedDrafts?: () => void
}

export const CalendarHeader = (props: CalendarHeaderProps): JSX.Element => {
  const {
    channels,
    isSingleChannel,
    onSelectChannels,
    onSelectTags,
    selectedChannelIds = [],
    disabledChannelIds,
    viewOnlyChannelIds,
    selectedTagIds = [],
    postTypeOptions = EMPTY_POST_TYPES,
    selectedPostTypes = EMPTY_POST_TYPES,
    onSelectPostTypes,
    viewOnly = false,
    showPostingTimesControl,
    showViewOnlyBadge = false,
    isUndatedDraftsOpen = false,
    isUndatedDraftsSidebarMounted = false,
    undatedDraftsSidebarId,
    undatedDraftsCount,
    onToggleUndatedDrafts,
  } = props
  const {
    wideRef: wideUndatedDraftsToggleRef,
    narrowRef: narrowUndatedDraftsToggleRef,
  } = useUndatedDraftsToggleFocusReturn(isUndatedDraftsOpen)
  const { createNewPostInComposer, triggerAttributes } = usePostComposer()
  const showOnWideHeader = pageHeaderStyles.hideOnNarrow
  const channel = isSingleChannel
    ? readFragment(FilterByChannel_Channel, channels).find(
        (candidateChannel) => candidateChannel.id === selectedChannelIds[0],
      )
    : undefined
  const undatedDraftsCountSuffix =
    typeof undatedDraftsCount === 'number' && undatedDraftsCount > 0
      ? ` (${undatedDraftsCount})`
      : ''
  const undatedDraftsAction = isUndatedDraftsOpen ? 'Hide' : 'Show'
  const undatedDraftsLabel = `${undatedDraftsAction} undated drafts${undatedDraftsCountSuffix}`
  const wideUndatedDraftsLabel = `${undatedDraftsAction} No Date drafts${undatedDraftsCountSuffix}`
  const undatedDraftsTooltip = 'Drafts and approvals without a scheduled date'
  const undatedDraftsControlsId = isUndatedDraftsSidebarMounted
    ? undatedDraftsSidebarId
    : undefined

  return (
    <PageHeader separator={false}>
      <PageHeader.Row size="full">
        {isSingleChannel ? <ChannelPageTitle /> : <PublishPageTitle />}
        <PageHeader.Actions>
          {showViewOnlyBadge && <ViewOnlyHeaderBadge />}
          <FeedbackWidget
            id="calendar-header-1"
            className={pageHeaderStyles.hideOnNarrow}
          />
          {isSingleChannel && (
            <GridPreviewButton
              className={pageHeaderStyles.hideOnNarrow}
              size="medium"
            />
          )}
          {isSingleChannel && !viewOnly && (
            <ChannelSettingsMenu
              channelId={channel?.id ?? ''}
              service={channel?.service ?? ''}
              channelType={channel?.type ?? ''}
              className={pageHeaderStyles.hideOnWide}
            />
          )}
          <div className={showOnWideHeader}>
            <PostListOrCalendarViewToggle className={styles.toggleViewAction} />
          </div>

          {!viewOnly && (
            <NewPostHeaderActions
              wrapTrigger={(trigger): JSX.Element => (
                <Slot.Root
                  {...triggerAttributes}
                  onClick={(): Promise<void> =>
                    createNewPostInComposer({
                      cta: 'publish-calendar-header-newPost-1',
                      channels:
                        selectedChannelIds && selectedChannelIds.length
                          ? selectedChannelIds
                          : [],
                      prefillPostData: {
                        tags: selectedTagIds,
                      },
                    })
                  }
                >
                  {trigger}
                </Slot.Root>
              )}
            />
          )}
        </PageHeader.Actions>
      </PageHeader.Row>
      <PageHeader.Row size="full">
        <div className={showOnWideHeader}>
          <CalendarControls />
        </div>
        <MobileCalendarDateLabel className={pageHeaderStyles.hideOnWide} />
        <PageHeader.Actions>
          {!isSingleChannel && (
            <div className={showOnWideHeader}>
              <FilterByChannel
                channels={channels}
                value={selectedChannelIds ?? []}
                onSelect={onSelectChannels}
                disabledChannelIds={disabledChannelIds}
                getDisabledChannelReason={(id) =>
                  getDisabledPublishChannelReason(
                    readFragment(FilterByChannel_Channel, channels).find(
                      (candidate) => candidate.id === id,
                    ),
                  )
                }
                viewOnlyChannelIds={viewOnlyChannelIds}
                viewOnlyChannelReason={VIEW_ONLY_PUBLISH_CHANNEL_TOOLTIP}
                className={styles.hideForCalendarActions}
              />
            </div>
          )}
          <div className={showOnWideHeader}>
            <TypeFilter
              className={
                isSingleChannel
                  ? styles.hideForMoreActions
                  : styles.hideForCalendarActions
              }
            />
          </div>
          <div className={showOnWideHeader}>
            <FilterByTag
              value={selectedTagIds ?? []}
              onSelect={onSelectTags}
              className={
                isSingleChannel
                  ? styles.hideForMoreActions
                  : styles.hideForCalendarActions
              }
            />
          </div>
          {postTypeOptions.length > 0 && onSelectPostTypes && (
            <div className={showOnWideHeader}>
              <FilterByPostType
                options={postTypeOptions}
                value={[...selectedPostTypes]}
                onSelect={onSelectPostTypes}
                splitName={POST_TYPE_FILTER_SPLIT}
                className={
                  isSingleChannel
                    ? styles.hideForMoreActions
                    : styles.hideForCalendarActions
                }
              />
            </div>
          )}
          {onToggleUndatedDrafts && (
            <>
              <div className={showOnWideHeader}>
                <UndatedDraftsToggle
                  ref={wideUndatedDraftsToggleRef}
                  variant="wide"
                  isOpen={isUndatedDraftsOpen}
                  label={wideUndatedDraftsLabel}
                  tooltip={undatedDraftsTooltip}
                  controlsId={undatedDraftsControlsId}
                  count={undatedDraftsCount}
                  onToggle={onToggleUndatedDrafts}
                >
                  No Date
                </UndatedDraftsToggle>
              </div>
              <Separator
                orientation="vertical"
                decorative
                className={clsx(
                  styles.noDateSeparator,
                  isSingleChannel
                    ? styles.hideForMoreActions
                    : styles.hideForCalendarActions,
                )}
              />
            </>
          )}
          {isSingleChannel ? (
            <div className={showOnWideHeader}>
              <TimezoneDisplay
                channelId={channel?.id}
                timezone={channel?.timezone}
                disabled={viewOnly}
                className={clsx(styles.hideForMoreActions, styles.lastAction)}
              />
            </div>
          ) : (
            <div className={showOnWideHeader}>
              <AllChannelsTimeZoneSelect
                className={clsx(
                  styles.hideForCalendarActions,
                  styles.lastAction,
                )}
              />
            </div>
          )}

          {isSingleChannel ? (
            <>
              <div className={showOnWideHeader}>
                <MoreActions
                  channelId={channel?.id ?? ''}
                  selectedTags={selectedTagIds}
                  onTagSelect={onSelectTags}
                  timezone={channel?.timezone}
                  showTypeFilter
                  data-tour-id="calendar-more-menu-tour-step"
                  className={styles.lastAction}
                />
              </div>
              <MoreActions
                channelId={channel?.id ?? ''}
                selectedTags={selectedTagIds}
                onTagSelect={onSelectTags}
                timezone={channel?.timezone}
                showTypeFilter
                triggerIcon={<ListFilterIcon />}
                className={pageHeaderStyles.hideOnWide}
              />
            </>
          ) : (
            <>
              <div className={showOnWideHeader}>
                {showPostingTimesControl && !viewOnly && (
                  <PostingTimesActions
                    className={clsx(
                      styles.hideForCalendarActions,
                      styles.lastAction,
                    )}
                  >
                    <ShowPostingTimesMenuItem />
                  </PostingTimesActions>
                )}
                <CalendarActions
                  channels={channels}
                  selectedChannels={selectedChannelIds}
                  onChannelSelect={onSelectChannels}
                  disabledChannelIds={disabledChannelIds}
                  selectedTags={selectedTagIds}
                  onTagSelect={onSelectTags}
                  postingTimesItem={
                    showPostingTimesControl && !viewOnly ? (
                      <ShowPostingTimesMenuItem />
                    ) : undefined
                  }
                  triggerIcon={<ListFilterIcon />}
                  className={clsx(
                    styles.showCalendarActions,
                    styles.lastAction,
                  )}
                />
              </div>
              <CalendarActions
                channels={channels}
                selectedChannels={selectedChannelIds}
                onChannelSelect={onSelectChannels}
                disabledChannelIds={disabledChannelIds}
                selectedTags={selectedTagIds}
                onTagSelect={onSelectTags}
                postingTimesItem={
                  showPostingTimesControl && !viewOnly ? (
                    <ShowPostingTimesMenuItem />
                  ) : undefined
                }
                triggerIcon={<ListFilterIcon />}
                className={pageHeaderStyles.hideOnWide}
              />
            </>
          )}
          <div className={pageHeaderStyles.hideOnWide}>
            <PostListOrCalendarViewToggle />
          </div>
          {onToggleUndatedDrafts && (
            <UndatedDraftsToggle
              ref={narrowUndatedDraftsToggleRef}
              variant="narrow"
              isOpen={isUndatedDraftsOpen}
              label={undatedDraftsLabel}
              tooltip={undatedDraftsTooltip}
              controlsId={undatedDraftsControlsId}
              onToggle={onToggleUndatedDrafts}
              className={pageHeaderStyles.hideOnWide}
            />
          )}
        </PageHeader.Actions>
      </PageHeader.Row>
    </PageHeader>
  )
}
